import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { mdy, mdyDow } from "@/lib/dateFmt";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Wheat, Sparkles, Handshake, Timer, Coins, TrendingUp, Info, Clock, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const TIER_STYLE = {
  golden:  { chip: "bg-amber-100 text-amber-900 border-amber-400",   btn: "bg-amber-50 hover:bg-amber-100 border-amber-400 text-amber-900",     ping: "bg-amber-200 text-amber-900",  label: "Golden" },
  urgent:  { chip: "bg-rose-100 text-rose-900 border-rose-400",       btn: "bg-rose-50 hover:bg-rose-100 border-rose-400 text-rose-900",         ping: "bg-rose-200 text-rose-900",    label: "Urgent" },
  soon:    { chip: "bg-emerald-100 text-emerald-900 border-emerald-300", btn: "bg-emerald-50 hover:bg-emerald-100 border-emerald-300 text-emerald-900", ping: "bg-emerald-200 text-emerald-900", label: "Soon" },
  distant: { chip: "bg-sky-100 text-sky-900 border-sky-300",           btn: "bg-sky-50 hover:bg-sky-100 border-sky-300 text-sky-900",             ping: "bg-sky-200 text-sky-900",       label: "Distant" },
};

const fmtTime = (t) => {
  if (!t) return "";
  try {
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    const hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
  } catch { return t; }
};

/** The Barn — 4-week rolling view with the employee's scheduled shifts side-by-side
 *  with pickable open shifts on their days off. Pickup buttons hit the standard
 *  /shift-pickup-requests flow via /game/plant so managers approve as normal. */
export default function BarnBoard({ facilityId, onPlanted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planting, setPlanting] = useState({});
  const [planted, setPlanted] = useState({});      // { key: true } for ~1.2s after success

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const r = await api.get(`/game/barn?facility_id=${facilityId}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load the barn");
    } finally { setLoading(false); }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const plant = async (day, o) => {
    const key = `${day.date}::${o.shift_code}`;
    setPlanting((p) => ({ ...p, [key]: true }));
    try {
      await api.post(`/game/plant/${encodeURIComponent(o.shift_code)}?date=${day.date}&facility_id=${facilityId}`);
      // Trigger the "planted!" flourish — a green burst + seedling animation.
      setPlanted((p) => ({ ...p, [key]: true }));
      setTimeout(() => setPlanted((p) => { const q = { ...p }; delete q[key]; return q; }), 1400);
      toast.success("🌱 Planted! Request sent to your manager", {
        description: `${o.shift_code} · ${mdyDow(day.date)} — approval will land in your Requests tab.`,
      });
      onPlanted?.();
      // Delay reload slightly so the animation actually plays before the button re-renders as "Pending".
      setTimeout(() => load(), 700);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't send the request");
    } finally {
      setPlanting((p) => ({ ...p, [key]: false }));
    }
  };

  const totalOpenAcrossBarn = useMemo(
    () => (data?.weeks || []).reduce((s, w) => s + (w.open_count || 0), 0),
    [data]
  );

  if (loading && !data) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Opening the barn doors…</div>;
  }

  return (
    <div className="relative max-w-5xl mx-auto" data-testid="barn-board">
      {/* --- The barn wrapper --- */}
      <div className="relative rounded-t-[36px] shadow-[0_18px_45px_-20px_rgba(0,0,0,0.35)] overflow-visible">
        {/* Gambrel black roof — reference-style: wide shallow top slope + steep lower slopes, dormers, rooster weathervane */}
        <div className="relative h-24 sm:h-28">
          <svg viewBox="0 0 600 112" preserveAspectRatio="xMidYMax meet" className="absolute inset-0 w-full h-full overflow-visible" aria-hidden="true">
            <defs>
              <linearGradient id="roofBlack" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#2f2f36"/>
                <stop offset="1" stopColor="#171719"/>
              </linearGradient>
            </defs>
            {/* Gambrel silhouette — softly rounded shoulders match the reference */}
            <path d="M0,112 L0,88 Q30,86 60,76 L150,50 Q225,30 300,26 Q375,30 450,50 L540,76 Q570,86 600,88 L600,112 Z"
                  fill="url(#roofBlack)" stroke="#0b0b0d" strokeWidth="2"/>
            {/* Ridge shadow line */}
            <path d="M20,88 L580,88" stroke="#0b0b0d" strokeWidth="1.5" opacity="0.85"/>
            {/* Two dormer windows (roof bumps) */}
            <g>
              <rect x="200" y="52" width="46" height="30" fill="#2a2a30" stroke="#0b0b0d" strokeWidth="1.5"/>
              <rect x="209" y="60" width="28" height="18" fill="#f5e9d8" stroke="#0b0b0d" strokeWidth="1.5"/>
              <line x1="223" y1="60" x2="223" y2="78" stroke="#0b0b0d" strokeWidth="1"/>
              <line x1="209" y1="69" x2="237" y2="69" stroke="#0b0b0d" strokeWidth="1"/>
            </g>
            <g>
              <rect x="354" y="52" width="46" height="30" fill="#2a2a30" stroke="#0b0b0d" strokeWidth="1.5"/>
              <rect x="363" y="60" width="28" height="18" fill="#f5e9d8" stroke="#0b0b0d" strokeWidth="1.5"/>
              <line x1="377" y1="60" x2="377" y2="78" stroke="#0b0b0d" strokeWidth="1"/>
              <line x1="363" y1="69" x2="391" y2="69" stroke="#0b0b0d" strokeWidth="1"/>
            </g>
            {/* Loft door with white X in the peak center */}
            <rect x="278" y="34" width="44" height="46" rx="2" fill="#c1441b" stroke="#f5e9d8" strokeWidth="2.5"/>
            <line x1="280" y1="36" x2="320" y2="78" stroke="#f5e9d8" strokeWidth="2.5"/>
            <line x1="320" y1="36" x2="280" y2="78" stroke="#f5e9d8" strokeWidth="2.5"/>

            {/* Rooster weathervane on the peak with N/E/S/W arms */}
            <g transform="translate(300,24)">
              <line x1="0" y1="0" x2="0" y2="-22" stroke="#1a1a1e" strokeWidth="2"/>
              {/* compass arms */}
              <line x1="-8" y1="-10" x2="8" y2="-10" stroke="#1a1a1e" strokeWidth="1.5"/>
              <line x1="-6" y1="-14" x2="-6" y2="-6" stroke="#1a1a1e" strokeWidth="1"/>
              <line x1="6" y1="-14" x2="6" y2="-6" stroke="#1a1a1e" strokeWidth="1"/>
              {/* N E S W markers */}
              <text x="0" y="-16" fontSize="4" textAnchor="middle" fill="#94a3b8" fontWeight="bold">N</text>
              <text x="9" y="-9" fontSize="4" textAnchor="middle" fill="#94a3b8" fontWeight="bold">E</text>
              <text x="0" y="-2" fontSize="4" textAnchor="middle" fill="#94a3b8" fontWeight="bold">S</text>
              <text x="-9" y="-9" fontSize="4" textAnchor="middle" fill="#94a3b8" fontWeight="bold">W</text>
              {/* Rooster silhouette */}
              <g transform="translate(0,-30)" fill="#1a1a1e">
                <ellipse cx="0" cy="0" rx="7" ry="4"/>
                <circle cx="6" cy="-3" r="3"/>
                <polygon points="8,-6 6,-9 11,-8"/>
                <polygon points="9,-2 13,-1 9,0" fill="#f59e0b"/>
                <polygon points="-7,-2 -12,-4 -12,-1"/>
                <line x1="-6" y1="4" x2="-6" y2="7" stroke="#1a1a1e" strokeWidth="1"/>
                <line x1="0" y1="4" x2="0" y2="7" stroke="#1a1a1e" strokeWidth="1"/>
              </g>
            </g>
          </svg>
        </div>

        {/* Barn body — red with white corner trims */}
        <div className="relative bg-[#c1441b] p-3 sm:p-4 border-x-[6px] border-b-[6px] border-[#f5e9d8] shadow-inner"
             style={{
               backgroundImage:
                 "repeating-linear-gradient(0deg, rgba(0,0,0,0.10) 0 1px, transparent 1px 32px), "
               + "repeating-linear-gradient(90deg, rgba(0,0,0,0.06) 0 1px, transparent 1px 90px)",
             }}
        >
          {/* Cream signboard — NO brown cross */}
          <div className="rounded-xl bg-[#f5e9d8] border-4 border-white p-3 sm:p-4 relative shadow-inner">
            <div className="relative flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-display font-black text-xl sm:text-2xl text-[#4c1e10] leading-tight">
                  The Barn
                </div>
                <div className="text-[11px] sm:text-xs text-[#7a3a20] mt-0.5">
                  Your next 4 weeks · pick up open shifts on your days off to earn coins &amp; XP
                </div>
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="rounded-full bg-white border border-[#5a1e0a]/30 px-3 py-1 text-[11px] font-bold text-[#4c1e10] tabular-nums" data-testid="barn-open-count">
                  <Sparkles size={11} className="inline mb-0.5 mr-1 text-amber-600" />
                  {totalOpenAcrossBarn} open shift{totalOpenAcrossBarn === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          </div>

          {/* Weekly rows */}
          <div className="mt-3 space-y-3">
            {(data?.weeks || []).map((w) => (
              <BarnWeekRow
                key={w.week_index}
                week={w}
                onPlant={plant}
                planting={planting}
                planted={planted}
              />
            ))}
          </div>
        </div>

        {/* Stone foundation */}
        <div className="h-3 bg-gradient-to-b from-[#8892a0] to-[#5a636e] border-x-[6px] border-b-[6px] border-[#f5e9d8] rounded-b-[10px]" />
      </div>

      {/* Grass base */}
      <div className="h-3 -mt-1 rounded-b-[24px] bg-gradient-to-b from-[#6cba48] to-[#4a9a35] shadow-inner" />

      <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5 flex-wrap">
        <Info size={11} />
        Requesting a pickup here is the same as clicking "Pick up" on your normal Requests page — your manager approves or denies, and it auto-updates your schedule.
      </div>
    </div>
  );
}

function BarnWeekRow({ week, onPlant, planting, planted }) {
  const style = TIER_STYLE[week.urgency?.tier] || TIER_STYLE.distant;
  const w0 = parseISO(week.week_start);
  const w1 = parseISO(week.week_end);
  return (
    <div className={`rounded-xl bg-[#f5e9d8] border-2 border-[#8a2d0f]/60 overflow-hidden shadow-sm`} data-testid={`barn-week-${week.week_index}`}>
      {/* Row header */}
      <div className="px-3 py-2 flex items-center gap-2 flex-wrap bg-[#e9d8bd] border-b-2 border-[#8a2d0f]/40">
        <div className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${style.chip}`}>
          <Sparkles size={11} /> {week.urgency?.label || style.label}
        </div>
        <div className="text-[11px] font-semibold text-[#4c1e10] tabular-nums">
          {mdy(w0)} – {mdy(w1)}
        </div>
        <div className="text-[11px] text-[#7a3a20] ml-auto tabular-nums">
          {week.my_shifts_count} on shift · {week.open_count} open
        </div>
      </div>

      {/* 7-day grid */}
      <div className="grid grid-cols-7 divide-x divide-[#8a2d0f]/20">
        {(week.days || []).map((d) => (
          <BarnDayCell key={d.date} d={d} onPlant={onPlant} planting={planting} planted={planted} />
        ))}
      </div>
    </div>
  );
}

function BarnDayCell({ d, onPlant, planting, planted }) {
  const scheduled = d.my_shift && !d.my_shift.is_absent;
  const off = !d.my_shift;
  return (
    <div
      className={`p-1.5 sm:p-2 min-h-[110px] flex flex-col relative ${
        d.is_today ? "bg-blue-50/70 ring-1 ring-blue-300 ring-inset" : d.is_past ? "bg-slate-50" : "bg-[#fff8ec]"
      }`}
      data-testid={`barn-day-${d.date}`}
    >
      <div className="flex items-baseline justify-between">
        <div className={`text-[9px] uppercase tracking-widest font-bold ${d.is_today ? "text-blue-700" : "text-[#7a3a20]"}`}>
          {d.dow}
        </div>
        <div className={`text-xs font-bold tabular-nums ${d.is_today ? "text-blue-800" : "text-[#4c1e10]"}`}>
          {format(parseISO(d.date), "d")}
        </div>
      </div>

      {/* My shift */}
      {scheduled && (
        <div className="mt-1 rounded-md bg-emerald-50 border border-emerald-300 text-emerald-900 px-1.5 py-1 text-[11px] font-bold" data-testid={`barn-my-${d.date}`}>
          <div className="tabular-nums">{d.my_shift.shift_code}</div>
          <div className="text-[9px] font-medium text-emerald-700 leading-tight">
            {fmtTime(d.my_shift.start)}–{fmtTime(d.my_shift.end)}
          </div>
        </div>
      )}
      {d.my_shift?.is_absent && (
        <div className="mt-1 rounded-md bg-red-50 border border-red-300 text-red-800 px-1.5 py-1 text-[11px] font-bold">
          A · Absent
        </div>
      )}

      {/* Open shifts I can plant (only on my days off) */}
      {off && (d.open_shifts || []).length > 0 && (
        <div className="mt-1 flex flex-col gap-1" data-testid={`barn-open-${d.date}`}>
          {d.open_shifts.map((o) => {
            const key = `${d.date}::${o.shift_code}`;
            const style = TIER_STYLE[o.reward?.tier] || TIER_STYLE.distant;
            const busy = !!planting[key];
            const justPlanted = !!(planted && planted[key]);
            const pending = o.has_pending_request;
            const disabled = !o.eligible || pending || busy || d.is_past;
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                onClick={() => onPlant(d, o)}
                data-testid={`barn-plant-${key}`}
                className={`relative overflow-hidden group text-left rounded-md border-2 px-1.5 py-1 text-[11px] font-bold shadow-sm transition-all disabled:opacity-70 disabled:cursor-not-allowed ${
                  justPlanted
                    ? "bg-emerald-50 border-emerald-500 text-emerald-900 ring-2 ring-emerald-300 animate-planted"
                    : pending
                    ? "bg-slate-50 border-slate-300 text-slate-500"
                    : `${style.btn} hover:-translate-y-0.5 hover:shadow`
                }`}
                title={o.eligible ? `Pick up ${o.shift_code}` : "Not on this facility's roster"}
              >
                {/* Planted flourish — green burst + rising seedling emoji */}
                {justPlanted && (
                  <>
                    <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-emerald-200/60 via-emerald-100/40 to-transparent animate-planted-burst" />
                    <span className="pointer-events-none absolute inset-0 grid place-items-center text-[22px] animate-planted-seedling">🌱</span>
                  </>
                )}
                <div className="flex items-center justify-between gap-1">
                  <span className="tabular-nums">{o.shift_code}</span>
                  {pending || justPlanted ? (
                    <CheckCircle2 size={11} className={justPlanted ? "opacity-100 text-emerald-700" : "opacity-70"} />
                  ) : (
                    <span className={`inline-flex items-center gap-0.5 text-[9px] font-black rounded-full px-1.5 py-0.5 ${style.ping}`}>
                      <Coins size={9} /> +{o.reward?.coins ?? 0}
                    </span>
                  )}
                </div>
                <div className="text-[9px] font-medium opacity-90 truncate">
                  {fmtTime(o.start)}–{fmtTime(o.end)}
                </div>
                {pending && !justPlanted && <div className="text-[9px] font-semibold mt-0.5">Pending</div>}
                {justPlanted && <div className="text-[9px] font-black mt-0.5 text-emerald-700">Planted!</div>}
                {!pending && !justPlanted && o.source === "coverage" && (
                  <div className="text-[9px] font-medium opacity-80 truncate">Cover · {o.source_employee_name?.split(" ")[0] || "gap"}</div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Off with nothing to pick */}
      {off && (d.open_shifts || []).length === 0 && !d.is_past && (
        <div className="mt-auto text-[10px] text-[#7a3a20]/70 italic">Off · nothing to plant</div>
      )}
      {off && d.is_past && (
        <div className="mt-auto text-[10px] text-slate-400 italic">—</div>
      )}
    </div>
  );
}
